<template>
  <main-dialog
    :show="uploadModal.show"
    :title="$t('upload')"
    max-width="580px"
    @close="close"
  >
    <!-- Drop Zone -->
    <div
      class="upl-dropzone"
      :class="{ 'upl-dropzone--dragging': isDragging, 'upl-dropzone--filled': selectedFiles.length > 0 }"
      @dragenter.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @dragover.prevent="isDragging = true"
      @drop.prevent="onDrop"
      @click="selectedFiles.length === 0 ? $refs.fileInput.click() : null"
    >
      <input
        ref="fileInput"
        type="file"
        multiple
        class="upl-hidden-input"
        @change="onFileInputChange"
      />

      <!-- Empty state -->
      <div v-if="selectedFiles.length === 0" class="upl-empty">
        <div class="upl-cloud-icon">
          <i class="bi bi-cloud-upload"></i>
        </div>
        <p class="upl-primary-text">{{ $t('dragAndDropFiles') }}</p>
        <p class="upl-secondary-text">
          {{ $t('or') }}
          <span class="upl-browse-link" @click.stop="$refs.fileInput.click()">{{ $t('browseFiles') }}</span>
        </p>
      </div>

      <!-- File list -->
      <div v-else class="upl-file-list" @click.stop>
        <div
          v-for="(file, idx) in selectedFiles"
          :key="idx"
          class="upl-file-item"
        >
          <div class="upl-file-icon">
            <i :class="getFileIcon(file.name)"></i>
          </div>
          <div class="upl-file-details">
            <span class="upl-file-name">{{ file.name }}</span>
            <span class="upl-file-size">{{ formatSize(file.size) }}</span>
          </div>
          <button class="upl-file-remove" @click="removeFile(idx)" :title="$t('remove')">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <button class="upl-add-more" @click="$refs.fileInput.click()">
          <i class="bi bi-plus-circle mr-1"></i>
          {{ $t('browseFiles') }}
        </button>
      </div>
    </div>

    <!-- Settings -->
    <div class="upl-settings">
      <div class="upl-setting-row">
        <div class="upl-setting-info">
          <span class="upl-setting-label">{{ $t('public') }}</span>
        </div>
        <button
          class="upl-toggle"
          :class="{ 'upl-toggle--on': isPublicUpload }"
          @click="isPublicUpload = !isPublicUpload"
          type="button"
          role="switch"
          :aria-checked="isPublicUpload.toString()"
        >
          <span class="upl-toggle-thumb"></span>
        </button>
      </div>

      <div class="upl-setting-row">
        <div class="upl-setting-info">
          <span class="upl-setting-label">{{ $t('replace') }}</span>
          <span class="upl-setting-desc">{{ $t('integrations.media.replaceDescription') }}</span>
        </div>
        <button
          class="upl-toggle"
          :class="{ 'upl-toggle--on': replaceExisting }"
          @click="replaceExisting = !replaceExisting"
          type="button"
          role="switch"
          :aria-checked="replaceExisting.toString()"
        >
          <span class="upl-toggle-thumb"></span>
        </button>
      </div>
    </div>

    <!-- Advanced options -->
    <div class="upl-advanced">
      <button class="upl-advanced-toggle" @click="showAdvanced = !showAdvanced" type="button">
        <i :class="['bi', showAdvanced ? 'bi-chevron-up' : 'bi-chevron-down']"></i>
        Advanced options
      </button>

      <div v-if="showAdvanced" class="upl-advanced-body">
        <div class="upl-adv-field">
          <label class="upl-adv-label">{{ $t('integrations.media.expiresAt') }}</label>
          <small class="upl-adv-desc">{{ $t('integrations.media.expiresAtDescription') }}</small>
          <b-form-datepicker
            v-model="uploadModal.expiresAt"
            dark
            reset-button
            :min="new Date()"
            class="mt-2"
          ></b-form-datepicker>
        </div>

        <div class="upl-adv-field">
          <div class="upl-adv-label-row">
            <label class="upl-adv-label mb-0">Metadata</label>
            <button class="upl-adv-add" @click="addMetadata" type="button">
              <i class="bi bi-plus"></i> {{ $t('add') }}
            </button>
          </div>
          <div v-if="uploadModal.metadata && uploadModal.metadata.length > 0" class="upl-meta-list mt-2">
            <div
              v-for="(d, idx) in uploadModal.metadata"
              :key="idx"
              class="upl-meta-row"
            >
              <b-input
                placeholder="Key"
                v-model="uploadModal.metadata[idx].key"
                class="upl-meta-input"
                size="sm"
              />
              <b-input
                placeholder="Value"
                v-model="uploadModal.metadata[idx].value"
                class="upl-meta-input"
                size="sm"
              />
              <button class="upl-meta-remove" @click="removeMetadata(idx)" type="button">
                <i class="bi bi-x"></i>
              </button>
            </div>
          </div>
          <p v-else class="upl-meta-empty">No metadata added yet.</p>
        </div>
      </div>
    </div>

    <template #footer>
      <button class="upl-btn-cancel" @click="close" type="button">
        {{ $t('cancel') }}
      </button>
      <button
        class="upl-btn-upload"
        :class="{ 'upl-btn-upload--disabled': selectedFiles.length === 0 }"
        :disabled="selectedFiles.length === 0"
        @click="upload"
        type="button"
      >
        <i class="bi bi-cloud-upload mr-1"></i>
        {{ $t('upload') }}
        <span v-if="selectedFiles.length > 0" class="upl-count-badge">{{ selectedFiles.length }}</span>
      </button>
    </template>
  </main-dialog>
</template>

<script>
export default {
  props: {
    uploadModal: {
      type: Object,
      required: true,
    },
    currentDir: {
      type: String,
      required: true,
    },
    currentLib: {
      type: Object,
      required: false,
    },
  },
  data: function () {
    return {
      isPublicUpload: false,
      replaceExisting: false,
      showAdvanced: false,
      isDragging: false,
      selectedFiles: [],
    };
  },
  watch: {
    'uploadModal.show': function (val) {
      if (!val) {
        this.reset();
      }
    },
  },
  methods: {
    close() {
      this.uploadModal.show = false;
    },
    reset() {
      this.selectedFiles = [];
      this.isPublicUpload = false;
      this.replaceExisting = false;
      this.showAdvanced = false;
      this.isDragging = false;
    },
    onDrop(e) {
      this.isDragging = false;
      var files = Array.from(e.dataTransfer.files);
      files.forEach(function (f) {
        this.selectedFiles.push(f);
      }, this);
    },
    onFileInputChange(e) {
      var files = Array.from(e.target.files);
      files.forEach(function (f) {
        this.selectedFiles.push(f);
      }, this);
      e.target.value = '';
    },
    removeFile(idx) {
      this.selectedFiles.splice(idx, 1);
    },
    getFileIcon(name) {
      var ext = name.split('.').pop().toLowerCase();
      var imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
      var videoExts = ['mp4', 'mov', 'avi', 'mkv', 'webm'];
      var audioExts = ['mp3', 'wav', 'ogg', 'flac', 'aac'];
      var archiveExts = ['zip', 'rar', 'tar', 'gz', '7z'];
      var codeExts = ['js', 'ts', 'vue', 'py', 'go', 'java', 'json', 'html', 'css'];
      if (imageExts.indexOf(ext) !== -1) return 'bi bi-file-image text-info';
      if (videoExts.indexOf(ext) !== -1) return 'bi bi-file-play text-warning';
      if (audioExts.indexOf(ext) !== -1) return 'bi bi-file-music text-success';
      if (ext === 'pdf') return 'bi bi-file-pdf text-danger';
      if (archiveExts.indexOf(ext) !== -1) return 'bi bi-file-zip text-secondary';
      if (codeExts.indexOf(ext) !== -1) return 'bi bi-file-code text-primary';
      if (ext === 'csv' || ext === 'xlsx' || ext === 'xls') return 'bi bi-file-spreadsheet text-success';
      if (ext === 'doc' || ext === 'docx') return 'bi bi-file-word text-primary';
      return 'bi bi-file-earmark text-muted';
    },
    formatSize(bytes) {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
      if (bytes < 1073741824) return (bytes / 1048576).toFixed(1) + ' MB';
      return (bytes / 1073741824).toFixed(1) + ' GB';
    },
    upload() {
      if (this.selectedFiles.length === 0) return;

      this.uploadModal.files = this.selectedFiles;

      const popup = this.$loadingPopup();

      const data = {
        files: this.selectedFiles,
        currentDir: this.currentDir,
        ref: this.currentLib.uuid,
        isPublic: this.isPublicUpload,
        replace: this.replaceExisting,
        expiresAt: this.uploadModal.expiresAt,
        metadata: this.metadata,
      };

      this.$media
        .upload(data)
        .then((resp) => {
          if (!resp) {
            this.$bvToast.toast(this.$t('uploadInBackground'), {
              variant: 'warning',
            });
            return;
          }

          this.$store.commit('APPEND_MEDIA', {
            path: this.currentDir,
            results: resp.data,
          });
          this.close();
        })
        .catch((error) => {
          this.$debugLog(error);

          if (error.response) {
            if (error.response.status == 500) {
              this.$serverErrorModal(error);
              return;
            }

            this.$mainDialog.msgBoxOk(error.response.data.message);
          }
        })
        .finally(() => {
          popup.hide();
        });
    },
    removeMetadata(idx) {
      this.uploadModal.metadata.splice(idx, 1);
    },
    addMetadata() {
      if (!this.uploadModal.metadata) {
        this.$set(this.uploadModal, 'metadata', []);
      }
      this.uploadModal.metadata.push({ key: '', value: '' });
    },
  },
  computed: {
    metadata() {
      var metaList = this.uploadModal.metadata || [];
      var formatted = {};
      metaList.forEach(function (v) {
        formatted[v.key] = v.value;
      });
      return formatted;
    },
  },
};
</script>

<style scoped>
/* ── Drop Zone ─────────────────────────────────────────────────── */
.upl-hidden-input {
  display: none;
}

.upl-dropzone {
  border: 2px dashed var(--border-default);
  border-radius: 10px;
  background: var(--bg-card-secondary);
  transition: border-color 0.2s ease, background 0.2s ease;
  cursor: pointer;
  overflow: hidden;
}

.upl-dropzone:hover,
.upl-dropzone--dragging {
  border-color: var(--primary, #5b8dee);
  background: rgba(91, 141, 238, 0.06);
}

.upl-dropzone--filled {
  cursor: default;
  border-style: solid;
  border-color: var(--border-default);
}

.upl-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1.5rem;
  text-align: center;
  user-select: none;
}

.upl-cloud-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: rgba(91, 141, 238, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  font-size: 1.75rem;
  color: var(--primary, #5b8dee);
}

.upl-primary-text {
  font-size: 0.9375rem;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
}

.upl-secondary-text {
  font-size: 0.8125rem;
  color: var(--text-muted, #8a9bb5);
  margin-bottom: 0;
}

.upl-browse-link {
  color: var(--primary, #5b8dee);
  cursor: pointer;
  font-weight: 500;
}

.upl-browse-link:hover {
  text-decoration: underline;
}

/* ── File list ─────────────────────────────────────────────────── */
.upl-file-list {
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.upl-file-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0.75rem;
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 8px;
  transition: border-color 0.15s ease;
}

.upl-file-item:hover {
  border-color: var(--primary, #5b8dee);
}

.upl-file-icon {
  font-size: 1.25rem;
  width: 28px;
  text-align: center;
  flex-shrink: 0;
}

.upl-file-details {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.upl-file-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.upl-file-size {
  font-size: 0.75rem;
  color: var(--text-muted, #8a9bb5);
}

.upl-file-remove {
  background: none;
  border: none;
  padding: 4px 6px;
  cursor: pointer;
  border-radius: 6px;
  color: var(--text-muted, #8a9bb5);
  transition: color 0.15s, background 0.15s;
  flex-shrink: 0;
  line-height: 1;
}

.upl-file-remove:hover {
  color: #e85d5d;
  background: rgba(232, 93, 93, 0.1);
}

.upl-add-more {
  align-self: flex-start;
  background: none;
  border: 1px dashed var(--border-default);
  color: var(--primary, #5b8dee);
  font-size: 0.8125rem;
  font-weight: 500;
  padding: 0.4rem 0.85rem;
  border-radius: 6px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  margin-top: 0.25rem;
}

.upl-add-more:hover {
  border-color: var(--primary, #5b8dee);
  background: rgba(91, 141, 238, 0.07);
}

/* ── Settings ──────────────────────────────────────────────────── */
.upl-settings {
  margin-top: 1rem;
  border: 1px solid var(--border-default);
  border-radius: 10px;
  overflow: hidden;
}

.upl-setting-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
}

.upl-setting-row + .upl-setting-row {
  border-top: 1px solid var(--border-default);
}

.upl-setting-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.upl-setting-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
}

.upl-setting-desc {
  font-size: 0.75rem;
  color: var(--text-muted, #8a9bb5);
}

/* Toggle switch */
.upl-toggle {
  position: relative;
  display: inline-block;
  width: 40px;
  height: 22px;
  border-radius: 11px;
  border: none;
  background: var(--border-default);
  cursor: pointer;
  transition: background 0.2s ease;
  flex-shrink: 0;
  padding: 0;
}

.upl-toggle--on {
  background: var(--primary, #5b8dee);
}

.upl-toggle-thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #ffffff;
  transition: transform 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  pointer-events: none;
}

.upl-toggle--on .upl-toggle-thumb {
  transform: translateX(18px);
}

/* ── Advanced section ──────────────────────────────────────────── */
.upl-advanced {
  margin-top: 0.75rem;
}

.upl-advanced-toggle {
  background: none;
  border: none;
  padding: 0.35rem 0;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-muted, #8a9bb5);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  transition: color 0.15s;
}

.upl-advanced-toggle:hover {
  color: var(--text-primary);
}

.upl-advanced-body {
  margin-top: 0.75rem;
  border: 1px solid var(--border-default);
  border-radius: 10px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.upl-adv-field {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.upl-adv-label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 2px;
}

.upl-adv-desc {
  font-size: 0.75rem;
  color: var(--text-muted, #8a9bb5);
}

.upl-adv-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.upl-adv-add {
  background: none;
  border: 1px solid var(--border-default);
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--primary, #5b8dee);
  padding: 3px 10px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}

.upl-adv-add:hover {
  border-color: var(--primary, #5b8dee);
  background: rgba(91, 141, 238, 0.07);
}

/* Metadata rows */
.upl-meta-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.upl-meta-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.upl-meta-input {
  flex: 1;
}

.upl-meta-remove {
  background: none;
  border: none;
  padding: 4px 6px;
  cursor: pointer;
  border-radius: 6px;
  color: var(--text-muted, #8a9bb5);
  transition: color 0.15s, background 0.15s;
  line-height: 1;
  flex-shrink: 0;
}

.upl-meta-remove:hover {
  color: #e85d5d;
  background: rgba(232, 93, 93, 0.1);
}

.upl-meta-empty {
  font-size: 0.8125rem;
  color: var(--text-muted, #8a9bb5);
  margin: 0.5rem 0 0;
}

/* ── Footer buttons ────────────────────────────────────────────── */
.upl-btn-cancel {
  background: none;
  border: 1px solid var(--border-default);
  border-radius: 8px;
  padding: 0.5rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}

.upl-btn-cancel:hover {
  border-color: var(--text-muted, #8a9bb5);
  background: var(--bg-card-secondary);
}

.upl-btn-upload {
  background: var(--primary, #5b8dee);
  border: none;
  border-radius: 8px;
  padding: 0.5rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  transition: opacity 0.15s, transform 0.1s;
}

.upl-btn-upload:hover:not(.upl-btn-upload--disabled) {
  opacity: 0.88;
  transform: translateY(-1px);
}

.upl-btn-upload--disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.upl-count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 99px;
  font-size: 0.75rem;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  font-weight: 700;
}
</style>
